import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import { useToast } from '../../contexts/ToastContext';

const PersonalConfig = () => {
  const { user: currentUser } = useAuth();
  const { showSuccess, showError, showConfirm } = useToast();
  const [users, setUsers] = useState([]);
  const [profesiones, setProfesiones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [userToDelete, setUserToDelete] = useState(null);
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    password: '',
    rol: 'PERSONAL',
    profesionId: '',
    activo: true
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [usersResponse, profesionesResponse] = await Promise.all([
        api.get('/personal'),
        api.get('/profesiones')
      ]);
      setUsers(usersResponse.data);
      setProfesiones(profesionesResponse.data);
    } catch (error) {
      console.error('Error cargando datos:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingUser) {
        await api.put(`/personal/${editingUser.id}`, formData);
      } else {
        await api.post('/personal', formData);
      }
      setShowForm(false);
      setEditingUser(null);
      resetForm();
      loadData();
    } catch (error) {
      console.error('Error guardando usuario:', error);
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      nombre: user.nombre,
      email: user.email,
      password: '',
      rol: user.rol,
      profesionId: user.profesionId?.toString() || '',
      activo: user.activo
    });
    setShowForm(true);
  };

  const handleDeleteRequest = (rowUser) => {
    setUserToDelete(rowUser);
    setDeletePassword('');
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!userToDelete) {
      return;
    }
    if (!deletePassword) {
      showError('Ingresa la contraseña del JEFE para confirmar');
      return;
    }
    try {
      await api.delete(`/personal/${userToDelete.id}`, {
        data: { password: deletePassword }
      });
      showSuccess('Usuario eliminado correctamente');
      setShowDeleteModal(false);
      setUserToDelete(null);
      setDeletePassword('');
      loadData();
    } catch (error) {
      console.error('Error eliminando usuario:', error);
      const message = error?.response?.data?.error || 'Error al eliminar usuario';
      showError(message);
    }
  };

  const resetForm = () => {
    setFormData({
      nombre: '',
      email: '',
      password: '',
      rol: 'PERSONAL',
      profesionId: '',
      activo: true
    });
  };

  const getRolBadge = (rol) => {
    const badges = {
      JEFE: { color: '#dc3545', text: 'Jefe' },
      ADMIN: { color: '#fd7e14', text: 'Administrador' },
      PERSONAL: { color: '#28a745', text: 'Personal' },
      COLABORADOR: { color: '#17a2b8', text: 'Colaborador' }
    };
    const badge = badges[rol] || { color: '#6c757d', text: rol };
    return (
      <span style={{
        backgroundColor: badge.color,
        color: 'white',
        padding: '4px 8px',
        borderRadius: '12px',
        fontSize: '0.75rem',
        fontWeight: 'bold'
      }}>
        {badge.text}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="text-center">
        <div className="spinner-border" role="status">
          <span className="sr-only">Cargando...</span>
        </div>
      </div>
    );
  }

  const filteredUsers = users.filter((rowUser) =>
    showInactive ? !rowUser.activo : rowUser.activo
  );

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h3>👨‍💼 Gestión de Personal</h3>
        <button
          className="btn btn-primary"
          onClick={() => {
            setShowForm(true);
            setEditingUser(null);
            resetForm();
          }}
        >
          <i className="fas fa-plus"></i> Nuevo Usuario
        </button>
      </div>

      {/* Formulario */}
      {showForm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1050,
          padding: '24px'
        }}>
          <div className="card" style={{ width: '900px', maxWidth: '100%', maxHeight: '90vh', overflowY: 'auto' }}>
            <div className="card-header">
              <h5>{editingUser ? 'Editar Usuario' : 'Nuevo Usuario'}</h5>
            </div>
            <div className="card-body">
              <form onSubmit={handleSubmit}>
              <div className="row">
                <div className="col-md-6">
                  <div className="form-group">
                    <label>Nombre *</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.nombre}
                      onChange={(e) => setFormData({...formData, nombre: e.target.value})}
                      required
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-group">
                    <label>Email *</label>
                    <input
                      type="email"
                      className="form-control"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      required
                    />
                  </div>
                </div>
              </div>
              <div className="row mt-3">
                <div className="col-md-4">
                  <div className="form-group">
                    <label>Contraseña {editingUser ? '(dejar en blanco para no cambiar)' : '*'}</label>
                    <input
                      type="password"
                      className="form-control"
                      value={formData.password}
                      onChange={(e) => setFormData({...formData, password: e.target.value})}
                      required={!editingUser}
                    />
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="form-group">
                    <label>Rol *</label>
                    <select
                      className="form-control"
                      value={formData.rol}
                      onChange={(e) => setFormData({...formData, rol: e.target.value})}
                      required
                    >
                      <option value="PERSONAL">Personal</option>
                      <option value="ADMIN">Administrador</option>
                      <option value="COLABORADOR">Colaborador</option>
                      {currentUser?.rol === 'JEFE' && <option value="JEFE">Jefe</option>}
                    </select>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="form-group">
                    <label>Profesión</label>
                    <select
                      className="form-control"
                      value={formData.profesionId}
                      onChange={(e) => setFormData({...formData, profesionId: e.target.value})}
                    >
                      <option value="">Seleccionar profesión</option>
                      {profesiones.map(prof => (
                        <option key={prof.id} value={prof.id}>{prof.nombre}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
              <div className="row mt-3">
                <div className="col-md-6">
                  <div className="form-check">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="activo"
                      checked={formData.activo}
                      onChange={(e) => setFormData({...formData, activo: e.target.checked})}
                    />
                    <label className="form-check-label" htmlFor="activo">
                      Usuario Activo
                    </label>
                  </div>
                </div>
              </div>
                <div className="mt-3">
                  <button type="submit" className="btn btn-primary">
                    {editingUser ? 'Actualizar' : 'Crear'} Usuario
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary ml-2"
                    onClick={() => {
                      setShowForm(false);
                      setEditingUser(null);
                      resetForm();
                    }}
                  >
                    Cancelar
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Tabla de usuarios */}
      <div className="card">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Lista de Usuarios</h5>
          <button
            className="btn btn-sm btn-outline-secondary"
            onClick={() => setShowInactive(!showInactive)}
          >
            {showInactive ? 'Ver activos' : 'Ver desactivados'}
          </button>
        </div>
        <div className="card-body">
          <div className="table-responsive">
            <table className="table table-striped" style={{ backgroundColor: 'white' }}>
              <thead>
                <tr style={{ backgroundColor: '#f8f9fa' }}>
                  <th style={{ color: '#495057', fontWeight: 'bold' }}>Nombre</th>
                  <th style={{ color: '#495057', fontWeight: 'bold' }}>Email</th>
                  <th style={{ color: '#495057', fontWeight: 'bold' }}>Rol</th>
                  <th style={{ color: '#495057', fontWeight: 'bold' }}>Profesión</th>
                  <th style={{ color: '#495057', fontWeight: 'bold' }}>Estado</th>
                  <th style={{ color: '#495057', fontWeight: 'bold' }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map(rowUser => (
                  <tr key={rowUser.id} style={{ color: '#212529' }}>
                    <td style={{ fontWeight: '500' }}>{rowUser.nombre}</td>
                    <td style={{ color: '#6c757d' }}>{rowUser.email}</td>
                    <td>{getRolBadge(rowUser.rol)}</td>
                    <td>{rowUser.profesion?.nombre || '-'}</td>
                    <td>
                      <span className={`badge ${rowUser.activo ? 'badge-success' : 'badge-danger'}`} style={{
                        backgroundColor: rowUser.activo ? '#28a745' : '#dc3545',
                        color: 'white',
                        padding: '6px 12px',
                        borderRadius: '4px',
                        fontSize: '0.875rem'
                      }}>
                        {rowUser.activo ? 'Activo' : 'Inactivo'}
                      </span>
                    </td>
                    <td style={{ minWidth: '120px' }}>
                      <div className="btn-group" role="group">
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={() => handleEdit(rowUser)}
                          style={{
                            marginRight: '5px',
                            padding: '4px 8px',
                            fontSize: '0.875rem'
                          }}
                          title="Editar usuario"
                        >
                          <i className="fas fa-edit"></i> Editar
                        </button>
                        {currentUser?.rol === 'JEFE' && rowUser.rol !== 'JEFE' && (
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => handleDeleteRequest(rowUser)}
                            style={{
                              padding: '4px 8px',
                              fontSize: '0.875rem'
                            }}
                            title="Eliminar usuario"
                          >
                            <i className="fas fa-trash"></i> Eliminar
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {showDeleteModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1050
        }}>
          <div className="card" style={{ width: '420px' }}>
            <div className="card-header">
              <h5>Confirmar eliminación</h5>
            </div>
            <div className="card-body">
              <p>
                Se eliminará definitivamente a <strong>{userToDelete?.nombre}</strong>.
              </p>
              <div className="form-group">
                <label>Contraseña del JEFE *</label>
                <input
                  type="password"
                  className="form-control"
                  value={deletePassword}
                  onChange={(e) => setDeletePassword(e.target.value)}
                  placeholder="Ingresa tu contraseña"
                />
              </div>
            </div>
            <div className="card-footer d-flex justify-content-end">
              <button
                className="btn btn-secondary mr-2"
                onClick={() => {
                  setShowDeleteModal(false);
                  setUserToDelete(null);
                  setDeletePassword('');
                }}
              >
                Cancelar
              </button>
              <button
                className="btn btn-danger"
                onClick={handleConfirmDelete}
              >
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PersonalConfig;
